import React, { useState, useEffect, useRef } from 'react';
import { checkDuplicate } from '../utils/api';

const CATEGORIES = [
  { value: 'lp_cogp',         label: 'LP / Co-GP (Equity)' },
  { value: 'lender',          label: 'Lender (Debt)' },
  { value: 'broker',          label: 'Broker (Deal Flow)' },
  { value: 'partner_service', label: 'Partner / Service Provider' },
  { value: 'seller_operator',  label: 'Seller / Operator (Off-Market)' },
];

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC',
  'ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

function DuplicateWarning({ results }) {
  if (!results || results.length === 0) return null;

  return (
    <div style={{ marginBottom: 14 }}>
      {results.map(r => (
        <div key={r.type} style={{
          padding: '8px 12px',
          borderRadius: 6,
          marginBottom: 6,
          background: r.severity === 'error' ? 'rgba(196,92,92,0.1)' : 'rgba(196,160,78,0.1)',
          border: `1px solid ${r.severity === 'error' ? 'rgba(196,92,92,0.4)' : 'rgba(196,160,78,0.4)'}`,
          fontSize: '0.8rem',
          color: r.severity === 'error' ? 'var(--color-danger)' : 'var(--color-warning)',
        }}>
          <strong>{r.severity === 'error' ? '⛔ Duplicate' : '⚠️ Possible duplicate'}</strong>
          {' '}{r.type === 'email' ? 'email' : r.type === 'phone' ? 'phone' : 'name'} match:
          {r.matches.map(m => (
            <span key={m.id} style={{ marginLeft: 6 }}>
              {m.first_name} {m.last_name} {m.organization ? `(${m.organization})` : ''}
            </span>
          ))}
          {r.severity === 'error' && <div style={{ marginTop: 3, opacity: 0.8 }}>Cannot create duplicate — resolve the existing contact instead.</div>}
        </div>
      ))}
    </div>
  );
}

export default function ContactFormModal({ contact, teamMembers, onSave, onClose }) {
  const isEdit = !!contact;

  const [form, setForm] = useState({
    first_name:            contact?.first_name || '',
    last_name:             contact?.last_name || '',
    organization:          contact?.organization || '',
    title:                 contact?.title || '',
    category:              contact?.category || 'broker',
    email:                 contact?.email || '',
    phone:                 contact?.phone || '',
    secondary_phone:       contact?.secondary_phone || '',
    address_line1:         contact?.address_line1 || '',
    address_line2:         contact?.address_line2 || '',
    city:                  contact?.city || '',
    state:                 contact?.state || '',
    zip:                   contact?.zip || '',
    relationship_owner_id: contact?.relationship_owner_id || (teamMembers[0]?.id || ''),
    secondary_owner_ids:   (contact?.secondary_owners || []).map(s => s.id),
    notes:                 contact?.notes || '',
    tags:                  contact?.tags?.join(', ') || '',
    next_follow_up:        contact?.next_follow_up || '',
  });

  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');
  const [duplicates, setDuplicates] = useState([]);
  const [hasHardBlock, setHardBlock] = useState(false);
  const [warnOverride, setWarnOverride] = useState(false); // "create anyway" for name soft warning
  const dupTimer = useRef(null);

  // Duplicate check whenever name/email/phone change (create mode only)
  useEffect(() => {
    if (isEdit) return;
    if (dupTimer.current) clearTimeout(dupTimer.current);
    dupTimer.current = setTimeout(async () => {
      const params = {};
      if (form.first_name && form.last_name) { params.first_name = form.first_name; params.last_name = form.last_name; }
      if (form.email) params.email = form.email;
      if (form.phone) params.phone = form.phone;
      if (!params.first_name && !params.email && !params.phone) { setDuplicates([]); setHardBlock(false); return; }
      try {
        const r = await checkDuplicate(params);
        setDuplicates(r.duplicates || []);
        setHardBlock(r.has_hard_block || false);
        setWarnOverride(false);
      } catch { /* silent */ }
    }, 500);
    return () => clearTimeout(dupTimer.current);
  }, [form.first_name, form.last_name, form.email, form.phone, isEdit]);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const toggleSecondaryOwner = (memberId) => {
    setForm(prev => {
      const ids = prev.secondary_owner_ids;
      return {
        ...prev,
        secondary_owner_ids: ids.includes(memberId)
          ? ids.filter(x => x !== memberId)
          : [...ids, memberId],
      };
    });
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setError('');

    if (!form.first_name.trim() || !form.last_name.trim()) { setError('First and last name are required.'); return; }
    if (!form.relationship_owner_id) { setError('Please assign a relationship owner.'); return; }
    if (hasHardBlock) { setError('A duplicate contact with the same email or phone exists.'); return; }
    // Soft warn: if there's a name duplicate and user hasn't overridden yet
    const hasNameWarn = duplicates.some(d => d.type === 'name');
    if (hasNameWarn && !warnOverride) { setWarnOverride(true); return; }

    setSaving(true);
    try {
      await onSave({
        ...form,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        secondary_owner_ids: form.secondary_owner_ids.filter(id => id !== form.relationship_owner_id),
        next_follow_up: form.next_follow_up || null,
      });
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const secondaryMembers = teamMembers.filter(m => m.id !== form.relationship_owner_id);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? 'Edit Contact' : 'New Contact'}</h2>
          <button className="btn-icon" onClick={onClose} style={{ fontSize: '1.2rem' }}>✕</button>
        </div>

        {/* Duplicate warnings */}
        {!isEdit && <DuplicateWarning results={duplicates} />}

        {/* Name-match soft warning + override prompt */}
        {warnOverride && !hasHardBlock && (
          <div style={{
            padding: '10px 14px', marginBottom: 14, borderRadius: 6,
            background: 'rgba(196,160,78,0.1)', border: '1px solid rgba(196,160,78,0.4)',
            fontSize: '0.82rem', color: 'var(--color-warning)',
          }}>
            A contact with this name already exists. Click <strong>Create Anyway</strong> to proceed, or cancel to go back.
          </div>
        )}

        {error && (
          <div style={{
            background: 'rgba(196,92,92,0.1)', border: '1px solid rgba(196,92,92,0.3)',
            borderRadius: 6, padding: '8px 12px',
            fontSize: '0.85rem', color: 'var(--color-danger)', marginBottom: 14,
          }}>{error}</div>
        )}

        {/* Core identity */}
        <div className="form-row">
          <div className="form-group">
            <label>First Name *</label>
            <input className="form-input" name="first_name" value={form.first_name} onChange={handleChange} autoFocus />
          </div>
          <div className="form-group">
            <label>Last Name *</label>
            <input className="form-input" name="last_name" value={form.last_name} onChange={handleChange} />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Organization</label>
            <input className="form-input" name="organization" value={form.organization} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Title</label>
            <input className="form-input" name="title" value={form.title} onChange={handleChange} />
          </div>
        </div>

        {/* Category + Primary owner */}
        <div className="form-row">
          <div className="form-group">
            <label>Category *</label>
            <select className="form-select" name="category" value={form.category} onChange={handleChange}>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Primary Owner *</label>
            <select className="form-select" name="relationship_owner_id" value={form.relationship_owner_id} onChange={handleChange}>
              <option value="">Select owner…</option>
              {teamMembers.map(m => <option key={m.id} value={m.id}>{m.display_name}</option>)}
            </select>
          </div>
        </div>

        {/* Secondary owners */}
        {secondaryMembers.length > 0 && (
          <div className="form-group">
            <label>Also Involved (Secondary)</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
              {secondaryMembers.map(m => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => toggleSecondaryOwner(m.id)}
                  style={{
                    padding: '3px 12px', borderRadius: 14, border: '1px solid var(--color-border)',
                    background: form.secondary_owner_ids.includes(m.id) ? 'var(--color-accent)' : 'transparent',
                    color: form.secondary_owner_ids.includes(m.id) ? 'white' : 'var(--color-text-secondary)',
                    fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'var(--font-ui)',
                  }}
                >
                  {m.display_name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Contact details */}
        <div className="form-row">
          <div className="form-group">
            <label>Email</label>
            <input className="form-input" name="email" type="email" value={form.email} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Phone</label>
            <input className="form-input" name="phone" type="tel" value={form.phone} onChange={handleChange} />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>City</label>
            <input className="form-input" name="city" value={form.city} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>State</label>
            <select className="form-select" name="state" value={form.state} onChange={handleChange}>
              <option value="">—</option>
              {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        {/* Follow-up date */}
        <div className="form-row">
          <div className="form-group">
            <label>Next Follow-up</label>
            <input className="form-input" name="next_follow_up" type="date" value={form.next_follow_up} onChange={handleChange} />
          </div>
        </div>

        {/* Notes + Tags */}
        <div className="form-group">
          <label>Notes</label>
          <textarea className="form-textarea" name="notes" value={form.notes} onChange={handleChange} rows={3} />
        </div>

        <div className="form-group">
          <label>Tags (comma-separated)</label>
          <input className="form-input" name="tags" value={form.tags} onChange={handleChange} placeholder="e.g. multifamily, southeast, preferred" />
        </div>

        <div className="modal-footer">
          <button className="btn" onClick={onClose}>Cancel</button>
          {warnOverride && !hasHardBlock ? (
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving…' : 'Create Anyway'}
            </button>
          ) : (
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving || hasHardBlock}>
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Contact'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
